export interface UsageLimitErrorData {
  error: 'cost_limit_exceeded';
  message: string;
  action_required: string;
  usage_page_url: string;
}

export const USAGE_LIMIT_EXCEEDED_EVENT = 'usageLimitExceeded';

export class CostLimitExceededError extends Error {
  readonly usageLimitData: UsageLimitErrorData;

  constructor(data: UsageLimitErrorData) {
    super(data.message);
    this.name = 'CostLimitExceededError';
    this.usageLimitData = data;
  }
}

function tryParseUsageLimitJson(raw: string): UsageLimitErrorData | null {
  try {
    const parsed = JSON.parse(raw) as Partial<UsageLimitErrorData>;
    if (parsed?.error === 'cost_limit_exceeded' && typeof parsed.message === 'string') {
      return {
        error: 'cost_limit_exceeded',
        message: parsed.message,
        action_required:
          parsed.action_required ??
          'Please upgrade your plan or wait until next month for your limit to reset.',
        usage_page_url: parsed.usage_page_url ?? '/account/usage',
      };
    }
  } catch {
    /* not JSON */
  }
  return null;
}

function extractJsonObject(raw: string): string | null {
  const match = raw.match(/\{[\s\S]*\}/);
  return match?.[0] ?? null;
}

/** Parse backend cost-limit payloads (429 detail, wrapped 400, ApiResponse message). */
export function parseUsageLimitError(payload: unknown): UsageLimitErrorData | null {
  if (payload == null) return null;

  if (typeof payload === 'string') {
    const direct = tryParseUsageLimitJson(payload);
    if (direct) return direct;
    const embedded = extractJsonObject(payload);
    return embedded ? tryParseUsageLimitJson(embedded) : null;
  }

  if (typeof payload !== 'object') return null;

  const obj = payload as Record<string, unknown>;
  if (obj.error === 'cost_limit_exceeded' && typeof obj.message === 'string') {
    return {
      error: 'cost_limit_exceeded',
      message: obj.message,
      action_required:
        typeof obj.action_required === 'string'
          ? obj.action_required
          : 'Please upgrade your plan or wait until next month for your limit to reset.',
      usage_page_url: typeof obj.usage_page_url === 'string' ? obj.usage_page_url : '/account/usage',
    };
  }

  for (const key of ['detail', 'message'] as const) {
    const value = obj[key];
    if (typeof value !== 'string') continue;
    const direct = tryParseUsageLimitJson(value);
    if (direct) return direct;
    const embedded = extractJsonObject(value);
    const fromEmbedded = embedded ? tryParseUsageLimitJson(embedded) : null;
    if (fromEmbedded) return fromEmbedded;
  }

  return null;
}

export function dispatchUsageLimitExceeded(data: UsageLimitErrorData): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(USAGE_LIMIT_EXCEEDED_EVENT, { detail: data }));
}

/** Returns parsed cost-limit data and opens the global modal when matched. */
export function notifyUsageLimitFromResponsePayload(payload: unknown): UsageLimitErrorData | null {
  const parsed = parseUsageLimitError(payload);
  if (parsed) dispatchUsageLimitExceeded(parsed);
  return parsed;
}

/** For fetch() streams that bypass axios interceptors. */
export async function notifyUsageLimitFromFetchResponse(
  response: Response,
): Promise<UsageLimitErrorData | null> {
  if (response.status !== 429 && response.status !== 400) return null;
  try {
    const text = await response.clone().text();
    let payload: unknown = text;
    try {
      payload = JSON.parse(text);
    } catch {
      /* plain-text detail */
    }
    return notifyUsageLimitFromResponsePayload(payload);
  } catch {
    return null;
  }
}
